import {
    getPickupLocations,
    hasPickupDriver,
    toMapPosition,
} from './dispatchUtils';

export const DEFAULT_STOP_SERVICE_MINUTES = 60;

const EARTH_RADIUS_KM = 6371;

const toTimestamp = (value) => {
    if (!value) return null;

    const timestamp = typeof value === 'number' ? value : new Date(value).getTime();
    return Number.isFinite(timestamp) ? timestamp : null;
};

const isCompletedLocation = (location) => (
    Boolean(location?.completed_at || location?.completedAt)
    || ['completed', 'complete'].includes(String(location?.status || '').toLowerCase())
);

const getScheduledTimestamp = (pickup) => toTimestamp(
    pickup?.scheduled_pickup_time || pickup?.waiting_for_driver_at
);

const compareScheduledPickups = (first, second) => {
    const firstScheduledAt = getScheduledTimestamp(first);
    const secondScheduledAt = getScheduledTimestamp(second);

    if (firstScheduledAt !== null && secondScheduledAt !== null) {
        if (firstScheduledAt !== secondScheduledAt) return firstScheduledAt - secondScheduledAt;
    } else if (firstScheduledAt !== null) {
        return -1;
    } else if (secondScheduledAt !== null) {
        return 1;
    }

    return String(first?.id ?? '').localeCompare(String(second?.id ?? ''), undefined, {numeric: true});
};

const getDistanceKm = (firstPosition, secondPosition) => {
    const first = toMapPosition(firstPosition);
    const second = toMapPosition(secondPosition);
    if (!first || !second) return 0;

    const toRadians = (degrees) => degrees * (Math.PI / 180);
    const latitudeDelta = toRadians(second.lat - first.lat);
    const longitudeDelta = toRadians(second.lng - first.lng);
    const firstLatitude = toRadians(first.lat);
    const secondLatitude = toRadians(second.lat);
    const haversine = (Math.sin(latitudeDelta / 2) ** 2)
        + (Math.cos(firstLatitude) * Math.cos(secondLatitude) * (Math.sin(longitudeDelta / 2) ** 2));
    const normalizedHaversine = Math.min(1, Math.max(0, haversine));

    return EARTH_RADIUS_KM * 2 * Math.atan2(
        Math.sqrt(normalizedHaversine),
        Math.sqrt(1 - normalizedHaversine)
    );
};

const normalizeDriveMinutes = (value) => {
    const minutes = Number(value);
    return Number.isFinite(minutes) && minutes > 0 ? minutes : 0;
};

export const isValidRouteStopOrder = (stops = []) => {
    const positionsByPickup = stops.reduce((positions, stop, index) => {
        const pickupKey = String(stop.pickupId);
        const pickupPositions = positions.get(pickupKey) || {origins: [], dropoffs: []};
        pickupPositions[stop.kind === 'origin' ? 'origins' : 'dropoffs'].push(index);
        positions.set(pickupKey, pickupPositions);
        return positions;
    }, new Map());

    return Array.from(positionsByPickup.values()).every(({origins, dropoffs}) => (
        !origins.length
        || !dropoffs.length
        || Math.max(...origins) < Math.min(...dropoffs)
    ));
};

const applyRouteStopOrder = (stops, stopOrder = []) => {
    if (!stopOrder.length) return stops;

    const stopsByKey = new Map(stops.map((stop) => [stop.key, stop]));
    const requestedStops = stopOrder.flatMap((key) => {
        const stop = stopsByKey.get(key);
        if (!stop) return [];
        stopsByKey.delete(key);
        return [stop];
    });
    const orderedStops = [...requestedStops, ...stops.filter((stop) => stopsByKey.has(stop.key))];

    return isValidRouteStopOrder(orderedStops) ? orderedStops : stops;
};

export const moveRouteStop = (stops = [], sourceKey, targetKey, placement = 'before') => {
    if (sourceKey === targetKey) return stops;

    const source = stops.find((stop) => stop.key === sourceKey);
    if (!source || !stops.some((stop) => stop.key === targetKey)) return stops;

    const reordered = stops.filter((stop) => stop.key !== sourceKey);
    const targetIndex = reordered.findIndex((stop) => stop.key === targetKey);
    const insertionIndex = placement === 'after' ? targetIndex + 1 : targetIndex;
    reordered.splice(insertionIndex, 0, source);

    return isValidRouteStopOrder(reordered) ? reordered : stops;
};

export const optimizeRouteStopOrder = (stops = [], startPosition = null) => {
    const pendingStops = [...stops];
    const optimizedStops = [];
    let currentPosition = toMapPosition(startPosition);

    while (pendingStops.length) {
        const eligibleStops = pendingStops.filter((stop) => (
            stop.kind === 'origin'
            || !pendingStops.some((pendingStop) => (
                String(pendingStop.pickupId) === String(stop.pickupId)
                && pendingStop.kind === 'origin'
            ))
        ));
        const comparisonPosition = currentPosition;
        const nextStop = [...eligibleStops].sort((first, second) => {
            if (comparisonPosition) {
                const distanceDifference = getDistanceKm(comparisonPosition, first.position)
                    - getDistanceKm(comparisonPosition, second.position);
                if (Math.abs(distanceDifference) > 0.001) return distanceDifference;
            }

            const scheduleDifference = (first.scheduledAt ?? Number.MAX_SAFE_INTEGER)
                - (second.scheduledAt ?? Number.MAX_SAFE_INTEGER);
            if (scheduleDifference) return scheduleDifference;
            return first.sequence - second.sequence;
        })[0];

        optimizedStops.push(nextStop);
        currentPosition = nextStop.position;
        pendingStops.splice(pendingStops.findIndex((stop) => stop.key === nextStop.key), 1);
    }

    return optimizedStops.map((stop) => stop.key);
};

export const getRoutePlanMetrics = (stops = [], startPosition = null) => {
    const routePositions = [toMapPosition(startPosition), ...stops.map((stop) => stop.position)].filter(Boolean);
    const directDistanceKm = routePositions.slice(1).reduce((distance, position, index) => (
        distance + getDistanceKm(routePositions[index], position)
    ), 0);
    const serviceMinutes = stops.reduce((total, stop) => total + Number(stop.serviceMinutes || 0), 0);
    const travelMinutes = stops.reduce((total, stop) => total + Number(stop.driveMinutes || 0), 0);
    const totalWaitingMinutes = stops.reduce((total, stop) => total + Number(stop.waitMinutes || 0), 0);
    const initialWaitingMinutes = Number(stops[0]?.waitMinutes || 0);
    const waitingMinutes = Math.max(0, totalWaitingMinutes - initialWaitingMinutes);
    const scheduledStopCount = stops.filter((stop) => stop.isScheduledTarget).length;
    const lateStops = stops.filter((stop) => Number(stop.lateMinutes || 0) > 0);
    const plannedStartAt = stops[0]?.startsAt ?? null;
    const plannedEndAt = stops[stops.length - 1]?.endsAt ?? null;
    const routeStartAt = stops[0]
        ? stops[0].startsAt - (normalizeDriveMinutes(stops[0].driveMinutes) * 60 * 1000)
        : null;

    return {
        stopCount: stops.length,
        directDistanceKm,
        serviceMinutes,
        travelMinutes,
        waitingMinutes,
        initialWaitingMinutes,
        scheduledStopCount,
        lateStopCount: lateStops.length,
        maxLateMinutes: lateStops.reduce(
            (maximum, stop) => Math.max(maximum, Number(stop.lateMinutes || 0)),
            0
        ),
        plannedStartAt,
        plannedEndAt,
        routeStartAt,
        totalDurationMinutes: routeStartAt !== null && plannedEndAt !== null
            ? Math.max(0, Math.round((plannedEndAt - routeStartAt) / (60 * 1000)))
            : 0,
    };
};

export const buildDriverRoutePlan = (
    pickups = [],
    {
        now = Date.now(),
        serviceMinutes = DEFAULT_STOP_SERVICE_MINUTES,
        stopOrder = [],
        travelDurationsMinutes = [],
    } = {}
) => {
    const planStart = toTimestamp(now) ?? Date.now();
    const normalizedServiceMinutes = Number(serviceMinutes);
    const serviceDurationMs = (
        Number.isFinite(normalizedServiceMinutes) && normalizedServiceMinutes > 0
            ? normalizedServiceMinutes
            : DEFAULT_STOP_SERVICE_MINUTES
    ) * 60 * 1000;
    const unscheduledStops = [...pickups].sort(compareScheduledPickups).flatMap((pickup) => {
        const locations = getPickupLocations(pickup);
        const deliveryStatusId = Number(pickup?.delivery_status?.id ?? pickup?.delivery_status_id);
        const scheduledAt = getScheduledTimestamp(pickup);
        const origins = deliveryStatusId >= 3
            ? []
            : locations.origins.filter((location) => !isCompletedLocation(location));
        const dropoffs = locations.dropoffs.filter((location) => !isCompletedLocation(location));

        return [
            ...origins.map((location, locationIndex) => ({kind: 'origin', location, locationIndex})),
            ...dropoffs.map((location, locationIndex) => ({kind: 'dropoff', location, locationIndex})),
        ].map(({kind, location, locationIndex}, index) => ({
            key: `${pickup.id}-${kind}-${location.id}-${locationIndex}`,
            pickupId: pickup.id,
            assigned: hasPickupDriver(pickup),
            kind,
            address: location.address || (kind === 'origin' ? 'Pickup location' : 'Drop-off location'),
            displayAddress: location.displayAddress
                || location.address
                || (kind === 'origin' ? 'Pickup location' : 'Drop-off location'),
            position: location.position,
            scheduledAt,
            sequence: index + 1,
        }));
    });
    const orderedStops = applyRouteStopOrder(unscheduledStops, stopOrder);
    const scheduledOriginPickupIds = new Set();
    let cursor = planStart;

    if (orderedStops[0] && orderedStops[0].scheduledAt !== null) {
        const firstDriveMinutes = normalizeDriveMinutes(travelDurationsMinutes[0]);
        const justInTimeDeparture = orderedStops[0].scheduledAt - (firstDriveMinutes * 60 * 1000);
        if (justInTimeDeparture > cursor) cursor = justInTimeDeparture;
    }

    return orderedStops.map((stop, index) => {
        const driveMinutes = normalizeDriveMinutes(travelDurationsMinutes[index]);
        cursor += driveMinutes * 60 * 1000;
        const arrivesAt = cursor;
        const pickupKey = String(stop.pickupId);
        const isScheduledTarget = (
            stop.kind === 'origin'
            && stop.scheduledAt !== null
            && !scheduledOriginPickupIds.has(pickupKey)
        );
        if (isScheduledTarget) scheduledOriginPickupIds.add(pickupKey);
        if (stop.scheduledAt !== null && stop.scheduledAt > cursor) cursor = stop.scheduledAt;

        const startsAt = cursor;
        const endsAt = startsAt + serviceDurationMs;
        const waitMinutes = Math.max(0, Math.round((startsAt - arrivesAt) / (60 * 1000)));
        const scheduleVarianceMinutes = isScheduledTarget
            ? Math.round((arrivesAt - stop.scheduledAt) / (60 * 1000))
            : null;
        cursor = endsAt;

        return {
            ...stop,
            sequence: index + 1,
            arrivesAt,
            startsAt,
            endsAt,
            driveMinutes,
            waitMinutes,
            isScheduledTarget,
            scheduleVarianceMinutes,
            lateMinutes: Math.max(0, scheduleVarianceMinutes || 0),
            serviceMinutes: serviceDurationMs / (60 * 1000),
        };
    });
};
